import { useEffect, useRef, useState } from 'react'
import { Form, Link, useLocation } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { useT } from '#app/utils/i18n.tsx'
import { useIsPending } from '#app/utils/misc.tsx'
import { LANDCODES, toonNummer, type TelFout } from '#app/utils/telefoon.ts'
import { AuthButton, AuthErrors, AuthShell } from './auth-shell.tsx'
import { GroepsappKnop } from './groepsapp-knop.tsx'

/**
 * De twee velden van de telefoondeur (W2), voor /login, /signup en /nummer:
 * eerst je nummer (de landcode los, +31 voorop), dan de code uit de sms.
 * De code vult zichzelf in waar dat kan: autocomplete="one-time-code" op
 * iOS en macOS, en WebOTP (navigator.credentials.get otp) in Chrome op
 * Android, dankzij de domeinregel onderaan de sms. Bij zes cijfers gaat het
 * formulier vanzelf. Elke stap is een gewoon formulier: zonder JavaScript
 * werkt de deur ook, alleen zonder vanzelf invullen.
 */

export function NummerVeld({
	fout,
	landcode = '+31',
	nummer = '',
	cta,
	children,
}: {
	fout?: TelFout
	landcode?: string
	nummer?: string
	cta: string
	children?: React.ReactNode
}) {
	const t = useT()
	const isPending = useIsPending()
	return (
		<Form method="POST" data-testid="tel-nummer-form">
			<HoneypotInputs />
			<input type="hidden" name="intent" value="stuur" />
			{children}
			<div className="veld">
				<label htmlFor="tel-nummer" className="veld-naam">
					{t('auth.tel_label')}
				</label>
				<div style={{ display: 'flex', gap: 8 }}>
					<select
						name="landcode"
						defaultValue={landcode}
						aria-label={t('auth.tel_landcode')}
						className="veld-invoer"
						data-testid="tel-landcode"
						style={{ width: 'auto', flex: '0 0 auto', paddingRight: 8 }}
					>
						{LANDCODES.map((code) => (
							<option key={code} value={code}>
								{code}
							</option>
						))}
					</select>
					<input
						id="tel-nummer"
						name="nummer"
						type="tel"
						inputMode="tel"
						autoComplete="tel"
						autoFocus
						required
						defaultValue={nummer}
						placeholder={t('auth.tel_placeholder')}
						className="veld-invoer"
						data-testid="tel-nummer"
						aria-invalid={fout ? true : undefined}
						aria-describedby={fout ? 'tel-fout' : undefined}
						style={{ flex: 1, minWidth: 0 }}
					/>
				</div>
				<AuthErrors errors={fout ? [t(fout)] : null} id="tel-fout" />
			</div>
			<AuthButton
				type="submit"
				pending={isPending}
				data-testid="tel-stuur"
				style={{ marginTop: 8 }}
			>
				{cta}
			</AuthButton>
		</Form>
	)
}

type OtpCredentialLike = { code?: string }

export function CodeVeld({
	phone,
	fout,
	opnieuw,
	cta,
	children,
}: {
	phone: string
	fout?: TelFout
	opnieuw?: boolean
	cta: string
	children?: React.ReactNode
}) {
	const t = useT()
	const isPending = useIsPending()
	const location = useLocation()
	const formRef = useRef<HTMLFormElement>(null)
	const [code, setCode] = useState('')
	// één keer versturen per code: WebOTP en typen (of de autofill) kunnen
	// tegelijk zes cijfers zien, en een tweede verzending zou de verbrande
	// code nog eens proberen. Na het antwoord (of als het veld weer minder
	// cijfers heeft) mag het opnieuw.
	const verstuurd = useRef(false)
	useEffect(() => {
		if (!isPending) verstuurd.current = false
	}, [isPending])
	const verstuurEenKeer = () => {
		if (verstuurd.current) return
		verstuurd.current = true
		requestAnimationFrame(() => formRef.current?.requestSubmit())
	}

	// WebOTP: Chrome op Android leest de sms met de domeinregel en vult de
	// code in na één tik op "Toestaan". Waar het niet bestaat, gebeurt er
	// niets en doet autocomplete="one-time-code" het werk.
	useEffect(() => {
		if (typeof window === 'undefined' || !('OTPCredential' in window)) return
		const stop = new AbortController()
		navigator.credentials
			.get({
				otp: { transport: ['sms'] },
				signal: stop.signal,
			} as CredentialRequestOptions)
			.then((credential) => {
				const gevonden = (credential as OtpCredentialLike | null)?.code
				if (!gevonden) return
				const cijfers = gevonden.replace(/\D/g, '').slice(0, 6)
				setCode(cijfers)
				// de staat landt vóór het versturen: het veld leest hem uit het DOM
				if (cijfers.length === 6) verstuurEenKeer()
			})
			.catch(() => {})
		return () => stop.abort()
	}, [])

	return (
		<>
			<Form
				method="POST"
				ref={formRef}
				data-testid="tel-code-form"
				onSubmit={() => {
					// ook de knop telt: daarna geen tweede verzending door WebOTP
					verstuurd.current = true
				}}
			>
				<HoneypotInputs />
				<input type="hidden" name="intent" value="code" />
				<input type="hidden" name="phone" value={phone} />
				{children}
				<div className="veld">
					<label htmlFor="tel-code" className="veld-naam">
						{t('auth.code')}
					</label>
					<input
						id="tel-code"
						name="code"
						autoComplete="one-time-code"
						inputMode="numeric"
						pattern="[0-9]{6}"
						maxLength={6}
						autoFocus
						required
						value={code}
						onChange={(e) => {
							const cijfers = e.target.value.replace(/\D/g, '').slice(0, 6)
							setCode(cijfers)
							if (cijfers.length < 6) verstuurd.current = false
							else if (!isPending) verstuurEenKeer()
						}}
						className="veld-invoer"
						data-testid="tel-code"
						aria-invalid={fout ? true : undefined}
						aria-describedby={fout ? 'tel-code-fout' : undefined}
						style={{ letterSpacing: '0.3em', fontSize: 20 }}
					/>
					<AuthErrors errors={fout ? [t(fout)] : null} id="tel-code-fout" />
				</div>
				<AuthButton
					type="submit"
					pending={isPending}
					data-testid="tel-code-cta"
					style={{ marginTop: 8 }}
				>
					{cta}
				</AuthButton>
			</Form>
			{opnieuw && !fout ? (
				<p className="zin-sub" role="status" data-testid="tel-opnieuw-gestuurd">
					{t('auth.tel_nieuwe_code_onderweg')}
				</p>
			) : null}
			<div
				style={{
					display: 'flex',
					gap: 16,
					flexWrap: 'wrap',
					alignItems: 'center',
					marginTop: 12,
				}}
			>
				<Form method="POST">
					<HoneypotInputs />
					<input type="hidden" name="intent" value="opnieuw" />
					<input type="hidden" name="phone" value={phone} />
					{children}
					<button
						type="submit"
						className="tekst-link"
						data-testid="tel-opnieuw"
						disabled={isPending}
						style={{
							background: 'none',
							border: 0,
							padding: 0,
							cursor: 'pointer',
						}}
					>
						{t('auth.nieuwe_code')}
					</button>
				</Form>
				<Link
					to={`${location.pathname}${location.search}`}
					className="tekst-link"
					data-testid="tel-ander-nummer"
				>
					{t('auth.tel_ander_nummer')}
				</Link>
			</div>
		</>
	)
}

/** De codestap als scherm: dezelfde kolom, de kop zegt waar de code
 *  naartoe ging (het nummer leesbaar in de zin). */
export function CodeStap({
	stand,
	cta,
	children,
}: {
	stand: { phone: string; fout?: TelFout; opnieuw?: boolean }
	cta: string
	children?: React.ReactNode
}) {
	const t = useT()
	return (
		<AuthShell
			title={t('auth.tel_code_title')}
			sub={t('auth.tel_code_sub', { phone: toonNummer(stand.phone) })}
		>
			<CodeVeld
				phone={stand.phone}
				fout={stand.fout}
				opnieuw={stand.opnieuw}
				cta={cta}
			>
				{children}
			</CodeVeld>
		</AuthShell>
	)
}

/**
 * De rustige uitnodigingsstand (bètapoort, R5-LP): geen fout maar een
 * uitleg, met de groepsapp-knop van de voorpagina. Op /signup zonder
 * uitnodiging, en na de code als het nummer nog geen account heeft en er
 * geen uitnodiging bij hoort (dan staat dat er eerst bij).
 */
export function UitnodigingStand({
	onbekendNummer,
	redirectTo,
	houd,
}: {
	onbekendNummer?: boolean
	redirectTo?: string | null
	/** het teken waarmee het bewezen nummer de mailstap overleeft */
	houd?: string | null
}) {
	const t = useT()
	return (
		<AuthShell title={t('auth.beta_title')} sub={t('auth.beta_body')}>
			{onbekendNummer ? (
				<p
					className="zin-sub"
					style={{ marginTop: 0, marginBottom: 12 }}
					data-testid="tel-onbekend"
				>
					{t('auth.tel_onbekend')}
				</p>
			) : null}
			<p className="zin-sub" style={{ marginTop: 0 }}>
				{t('auth.beta_hint')}
			</p>
			<div style={{ marginTop: 24 }}>
				<GroepsappKnop className="btn btn-zacht" testId="beta-groepsapp" />
			</div>
			{onbekendNummer ? (
				<AlEenAccount redirectTo={redirectTo} houd={houd} />
			) : null}
		</AuthShell>
	)
}

/**
 * Voor wie al een account met e-mail heeft en toch zijn nummer typte (W2
 * review): log in met je mail, dan komt het net bewezen nummer er vanzelf
 * bij (het bewijs reist mee in de verificatiekoek, login.email.tsx).
 */
export function AlEenAccount({
	redirectTo,
	houd,
}: {
	redirectTo?: string | null
	houd?: string | null
}) {
	const t = useT()
	const zoek = new URLSearchParams()
	// alleen langs deze knop overleeft het bewezen nummer de mailstap
	// (telefoon-deur.server.ts, bewijsVoorMail)
	if (houd) zoek.set('houd', houd)
	if (redirectTo) zoek.set('redirectTo', redirectTo)
	const query = zoek.toString()
	return (
		<p className="zin-sub" style={{ marginTop: 20 }}>
			<Link
				to={query ? `/login/email?${query}` : '/login/email'}
				className="tekst-link"
				data-testid="tel-al-account"
			>
				{t('auth.tel_al_account')}
			</Link>
		</p>
	)
}
